import { BranchStep, StepTypes } from '../../dorian-shared/types/branch/BranchStep';
import { Branch } from '../../dorian-shared/types/branch/Branch';

export interface CursorPosition {
  branchId: number;
  /** 0-based step index. null = cursor is on the node but no step exists (empty node). */
  stepIndex: number | null;
}

export interface CursorState {
  cursor: CursorPosition;
  /** Oldest-first. Pop from the end to go back. Capped at CURSOR_HISTORY_CAP entries. */
  historyBack: CursorPosition[];
  /** Most-recently-popped first. Pop from the end to go forward / redo. */
  historyForward: CursorPosition[];
  /**
   * Live unsaved node snapshot — the most recent state published by an edit voronka.
   * Set on STEP_EDITED (field edit) and on structural facts (STEP_ADDED,
   * STEP_MOVED, STEPS_DELETED) so the adapter never serves a stale draft after
   * structural operations. Cleared on NODE_OPENED — prevents cancelled edits from
   * leaking into the next form open for the same node.
   * Not stored in history (this is data, not navigation).
   */
  draft: Branch | null;
}

export const CURSOR_HISTORY_CAP = 10000;

export type CursorAction =
  | { type: 'SET_STEP'; stepIndex: number }
  | { type: 'NAVIGATE'; branchId: number; stepIndex?: number | null }
  | { type: 'GO_BACK' }
  | { type: 'GO_FORWARD'; fallback?: CursorPosition }
  | { type: 'STEP_EDITED'; branchId: number; stepIndex: number; node: Branch }
  | { type: 'STEP_ADDED'; branchId: number; stepIndex: number; node: Branch }
  | { type: 'STEP_MOVED'; branchId: number; fromIndex: number; toIndex: number; node: Branch }
  | { type: 'STEPS_DELETED'; branchId: number; stepIndices: number[]; node: Branch }
  | { type: 'STEP_FOCUSED'; branchId: number; stepIndex: number }
  | { type: 'NODE_EDITED'; branchId: number; node: Branch }
  | { type: 'NODE_OPENED'; branchId: number; stepIndex: number | null }
  | { type: 'NODE_CLOSED'; branchId: number; cacheStepsCount: number | null }
  | { type: 'RESET_NAVIGATION_HISTORY' };

function cappedPush(stack: CursorPosition[], entry: CursorPosition): CursorPosition[] {
  const next = [...stack, entry];
  return next.length > CURSOR_HISTORY_CAP ? next.slice(next.length - CURSOR_HISTORY_CAP) : next;
}

// Preserves unsaved node-level edits (e.g. location) when merging a step snapshot for the same node.
function mergeStepSnapshot(draft: Branch | null, branchId: number, snapshot: Branch): Branch {
  if (draft !== null && draft.id === branchId) {
    // Keep node-level fields from the draft; accept the incoming steps array.
    return { ...draft, steps: snapshot.steps };
  }
  return snapshot;
}

// Preserves unsaved step edits when merging a node-level snapshot for the same node.
function mergeNodeSnapshot(draft: Branch | null, branchId: number, snapshot: Branch): Branch {
  if (draft !== null && draft.id === branchId) {
    // Keep the in-progress steps; accept updated node-level fields.
    return { ...snapshot, steps: draft.steps };
  }
  return snapshot;
}

export function cursorReducer(state: CursorState, action: CursorAction): CursorState {
  switch (action.type) {
    case 'SET_STEP': {
      return {
        ...state,
        historyBack: cappedPush(state.historyBack, state.cursor),
        historyForward: [],
        cursor: { branchId: state.cursor.branchId, stepIndex: action.stepIndex },
      };
    }

    case 'NAVIGATE': {
      // undefined means "go to first step" (default for a different node).
      // null means "node has no steps" (explicit empty-node frame).
      // Using === undefined instead of ?? so null is preserved as-is.
      const targetStep = action.stepIndex === undefined ? 0 : action.stepIndex;
      // Same-node NAVIGATE: update stepIndex only, no new history entry.
      // Keeps historyBack clean when the caller re-navigates to the node that is
      // already current (e.g. opening a form for a node the cursor already points to).
      if (action.branchId === state.cursor.branchId) {
        // stepIndex omitted on same-node means "stay at current step" — no-op.
        // Prevents single-click on the active node from resetting the emulator to step 0
        // when the author has already navigated forward inside it.
        if (action.stepIndex === undefined) {
          // Disambiguate the two meanings of a null cursor: here null means "not yet
          // initialized" (restored after reload with no form open), so "stay" has no step
          // to stay on — seed the first step instead of no-opping forward forever. An
          // empty node arrives with an explicit null payload (not undefined), so it skips
          // this branch and stays null below.
          if (state.cursor.stepIndex === null) {
            return {
              ...state,
              cursor: { branchId: state.cursor.branchId, stepIndex: 0 },
            };
          }
          return state;
        }
        if (targetStep === state.cursor.stepIndex) return state;
        return {
          ...state,
          cursor: { branchId: state.cursor.branchId, stepIndex: targetStep },
        };
      }
      return {
        ...state,
        historyBack: cappedPush(state.historyBack, state.cursor),
        historyForward: [],
        cursor: { branchId: action.branchId, stepIndex: targetStep },
      };
    }

    case 'GO_BACK': {
      if (state.historyBack.length === 0) return state;
      const prev = state.historyBack[state.historyBack.length - 1];
      return {
        ...state,
        historyBack: state.historyBack.slice(0, -1),
        historyForward: cappedPush(state.historyForward, state.cursor),
        cursor: prev,
      };
    }

    case 'GO_FORWARD': {
      if (state.historyForward.length > 0) {
        const next = state.historyForward[state.historyForward.length - 1];
        return {
          ...state,
          historyBack: cappedPush(state.historyBack, state.cursor),
          historyForward: state.historyForward.slice(0, -1),
          cursor: next,
        };
      }
      if (action.fallback != null) {
        return {
          ...state,
          historyBack: cappedPush(state.historyBack, state.cursor),
          historyForward: [],
          cursor: action.fallback,
        };
      }
      return state;
    }

    case 'STEP_EDITED': {
      return {
        ...state,
        cursor: { branchId: action.branchId, stepIndex: action.stepIndex },
        draft: mergeStepSnapshot(state.draft, action.branchId, action.node),
      };
    }

    case 'STEP_ADDED': {
      // Draft is written even for alien nodes — adapter matches by id, so it is never served to the wrong branch.
      const withDraft = { ...state, draft: mergeStepSnapshot(state.draft, action.branchId, action.node) };
      if (action.branchId !== state.cursor.branchId) return withDraft;
      return {
        ...withDraft,
        cursor: { branchId: action.branchId, stepIndex: action.stepIndex },
      };
    }

    case 'STEP_MOVED': {
      const withDraft = { ...state, draft: mergeStepSnapshot(state.draft, action.branchId, action.node) };
      if (action.branchId !== state.cursor.branchId) return withDraft;
      const cur = state.cursor.stepIndex;
      if (cur === null) return withDraft;
      const { fromIndex, toIndex } = action;
      let next = cur;
      if (cur === fromIndex) {
        next = toIndex;
      } else if (fromIndex < toIndex) {
        if (cur > fromIndex && cur <= toIndex) next = cur - 1;
      } else if (cur >= toIndex && cur < fromIndex) {
        next = cur + 1;
      }
      if (next === cur) return withDraft;
      return {
        ...withDraft,
        cursor: { ...state.cursor, stepIndex: next },
      };
    }

    case 'STEPS_DELETED': {
      const withDraft = { ...state, draft: mergeStepSnapshot(state.draft, action.branchId, action.node) };
      if (action.branchId !== state.cursor.branchId) return withDraft;
      const deleted = new Set(action.stepIndices);
      const cur = state.cursor.stepIndex;
      if (cur === null) return withDraft;
      // Deleted indices strictly below cursor push it up by that count.
      const deletedBelow = action.stepIndices.filter((i) => i < cur).length;
      if (deleted.has(cur)) {
        const surviving = cur - deletedBelow - 1;
        // No surviving steps at or before cursor — node is now empty or cursor was first.
        if (surviving < 0) {
          return { ...withDraft, cursor: { ...state.cursor, stepIndex: null } };
        }
        return {
          ...withDraft,
          cursor: { ...state.cursor, stepIndex: surviving },
        };
      }
      if (deletedBelow === 0) return withDraft;
      return {
        ...withDraft,
        cursor: { ...state.cursor, stepIndex: cur - deletedBelow },
      };
    }

    case 'STEP_FOCUSED': {
      if (action.branchId !== state.cursor.branchId) return state;
      if (action.stepIndex === state.cursor.stepIndex) return state;
      return {
        ...state,
        cursor: { branchId: action.branchId, stepIndex: action.stepIndex },
      };
    }

    case 'NODE_EDITED': {
      return {
        ...state,
        draft: mergeNodeSnapshot(state.draft, action.branchId, action.node),
      };
    }

    case 'NODE_OPENED': {
      if (action.branchId === state.cursor.branchId) {
        // Same node re-opened — draft cleared to prevent cancelled edits from leaking.
        // First open (cursor.stepIndex still null) must adopt the action's step so forward
        // has a target; an author-chosen numeric step is kept (single-click must not reset to 0).
        if (state.cursor.stepIndex === null) {
          return {
            ...state,
            cursor: { branchId: action.branchId, stepIndex: action.stepIndex },
            draft: null,
          };
        }
        return state.draft === null ? state : { ...state, draft: null };
      }
      // NODE_OPENED does not push to historyBack — opening a node is not author navigation.
      return {
        ...state,
        cursor: { branchId: action.branchId, stepIndex: action.stepIndex },
        draft: null,
      };
    }

    case 'NODE_CLOSED': {
      // Draft is cleared so cancelled edits do not bleed into the next open for the same node.
      // stepIndex is clamped to the cache node's last valid index: a STEP_ADDED while the form
      // was open moved the cursor forward, but the server cache does not have that step after
      // cancel — so the out-of-range index would cause selectRenderState to return null
      // (phantom last frame stays frozen on screen).
      const { cacheStepsCount } = action;
      const clampedStep = ((): number | null => {
        const cur = state.cursor.stepIndex;
        if (cur === null) return null;
        // cacheStepsCount belongs to the node being closed; clamping only makes sense while
        // the cursor still points at that node. After a cross-node GO_BACK/forward the cursor
        // has already moved, so leave it untouched (else it snaps to the old node's range).
        if (action.branchId !== state.cursor.branchId) return cur;
        if (cacheStepsCount === null || cacheStepsCount === 0) return null;
        return Math.min(cur, cacheStepsCount - 1);
      })();
      const cursorChanged = clampedStep !== state.cursor.stepIndex;
      if (state.draft === null && !cursorChanged) return state;
      return {
        ...state,
        draft: null,
        cursor: cursorChanged
          ? { ...state.cursor, stepIndex: clampedStep }
          : state.cursor,
      };
    }

    case 'RESET_NAVIGATION_HISTORY': {
      // Intro is the start of the story: there is nowhere to go back or forward to.
      if (state.historyBack.length === 0 && state.historyForward.length === 0) return state;
      return { ...state, historyBack: [], historyForward: [] };
    }

    default:
      return state;
  }
}

const NAVIGATION_BLOCKING_STEP_TYPES = new Set<StepTypes>([
  StepTypes.Choice,
  StepTypes.Hub,
]);

export function isNavigationBlocking(step: BranchStep | null | undefined): boolean {
  if (!step) return false;
  return NAVIGATION_BLOCKING_STEP_TYPES.has(step.stepTypeId);
}

/**
 * ADR-013: `›` is disabled on the last step of an Ending node.
 * An Ending node is identified by its last step having stepTypeId === StepTypes.Ending.
 */
export function isEndingStep(step: BranchStep | null | undefined): boolean {
  if (!step) return false;
  return step.stepTypeId === StepTypes.Ending;
}

/**
 * Compute the fallback cursor position for GO_FORWARD when historyForward is empty.
 * Returns null when `›` should be disabled (blocked step type, last Ending step, no exit).
 *
 * The caller passes the result as `action.fallback` — keeping branch data out of the reducer.
 * This function is pure and exported so it can be unit-tested independently.
 */
export function computeForwardFallback(
  node: Branch & { steps: BranchStep[] },
  cursor: CursorPosition,
): CursorPosition | null {
  const { steps } = node;
  if (!steps || steps.length === 0) return null;

  // No step selected (empty node) or out-of-bounds: cannot compute a meaningful fallback.
  if (cursor.stepIndex === null || cursor.stepIndex < 0 || cursor.stepIndex >= steps.length) return null;

  const currentStep = steps[cursor.stepIndex] ?? null;

  // Disabled: current step type does not allow linear forward navigation.
  if (isNavigationBlocking(currentStep)) return null;

  const isLastStep = cursor.stepIndex === steps.length - 1;

  if (!isLastStep) {
    // Normal forward: advance to the next step in the same node.
    return { branchId: cursor.branchId, stepIndex: cursor.stepIndex + 1 };
  }

  // Last step of the node.
  const lastStep = steps[steps.length - 1];

  // Disabled: the last step is an Ending (story terminates here).
  if (isEndingStep(lastStep)) return null;

  // Follow the node's default exit to the next node (stepIndex 0).
  if (!node.gotoBranchId) return null;
  return { branchId: node.gotoBranchId, stepIndex: 0 };
}
